import {
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	useTheme,
} from "@mui/material";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { type MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useDeletePortfolioMutation } from "web_myplann/Api/Portfolio/Mutations/useDeletePortfolioMutation";
import { useDeletePropertyMutation } from "web_myplann/Api/Portfolio/Mutations/useDeletePropertyMutation";
import type {
	ClientPortfolioDomainEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DeleteItemDrawer } from "web_myplann/Portfolio/Components/DeleteItemDrawer";
import { portfolioDomainRegistry } from "web_myplann/Portfolio/Helpers/portfolioDomainRegistry";
import { propertyTypeRegistry } from "web_myplann/Portfolio/Helpers/propertyTypeRegistry";

export enum PortfolioDetailKind {
	Property = "property",
	Portfolio = "portfolio",
}

type PortfolioDetailMenuProps =
	| {
			kind: PortfolioDetailKind.Property;
			id: number;
			propertyType: ClientPropertyTypeEnum;
			title: string;
	  }
	| {
			kind: PortfolioDetailKind.Portfolio;
			id: number;
			domain: ClientPortfolioDomainEnum;
			title: string;
	  };

const resolveEditPath = (
	props: PortfolioDetailMenuProps,
): string | undefined =>
	props.kind === PortfolioDetailKind.Property
		? propertyTypeRegistry[props.propertyType]?.editRoute(props.id)
		: portfolioDomainRegistry[props.domain]?.editRoute(props.id);

export const PortfolioDetailMenu = (props: PortfolioDetailMenuProps) => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const open = anchorEl !== null;

	const { mutate: deleteProperty, isPending: isDeletingProperty } =
		useDeletePropertyMutation();
	const { mutate: deletePortfolio, isPending: isDeletingPortfolio } =
		useDeletePortfolioMutation();

	const isPending = isDeletingProperty || isDeletingPortfolio;

	const editPath = resolveEditPath(props);

	const deleteCopy =
		props.kind === PortfolioDetailKind.Property
			? Resources.Portfolio.Actions.DeleteProperty
			: Resources.Portfolio.Actions.DeletePortfolio;

	const itemTitle = props.title;

	const handleOpen = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleEdit = () => {
		handleClose();
		if (editPath) {
			navigate(editPath);
		}
	};

	const handleDelete = () => {
		handleClose();
		setDeleteOpen(true);
	};

	const handleDeleteSuccess = () => {
		setDeleteOpen(false);
		toast.success(t(deleteCopy.successToast));
		navigate(Routing.Portfolio.PortfolioList.path());
	};

	const handleConfirmDelete = () => {
		if (props.kind === PortfolioDetailKind.Property) {
			deleteProperty(props.id, { onSuccess: handleDeleteSuccess });
		} else {
			deletePortfolio(props.id, { onSuccess: handleDeleteSuccess });
		}
	};

	return (
		<>
			<IconButton onClick={handleOpen} size="small">
				<MoreVertical size={20} color={theme.palette.grey[600]} />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{
					paper: {
						sx: {
							borderRadius: "16px",
						},
					},
					list: {
						sx: {
							p: 0,
							"&& .MuiDivider-root": { my: 0 },
							"&& .MuiListItemIcon-root": { minWidth: 0 },
							"& .MuiMenuItem-root": {
								backgroundColor: "background.default",
							},
						},
					},
				}}
			>
				{editPath && (
					<MenuItem
						key="edit"
						onClick={handleEdit}
						sx={{ gap: 1.2, py: 1.2, px: 1.6 }}
					>
						<ListItemIcon>
							<Pencil size={16} />
						</ListItemIcon>
						<ListItemText>
							{t(Resources.Portfolio.Actions.editContract)}
						</ListItemText>
					</MenuItem>
				)}
				{editPath && <Divider key="divider" />}
				<MenuItem
					key="delete"
					onClick={handleDelete}
					sx={{ gap: 1.2, py: 1.2, px: 1.6 }}
				>
					<ListItemIcon>
						<Trash2 size={theme.iconSize.sm} color={theme.palette.error.main} />
					</ListItemIcon>
					<ListItemText
						slotProps={{ primary: { color: theme.palette.error.main } }}
					>
						{t(Resources.Portfolio.Actions.deleteContract)}
					</ListItemText>
				</MenuItem>
			</Menu>
			<DeleteItemDrawer
				open={deleteOpen}
				itemTitle={itemTitle}
				namespace={deleteCopy}
				isPending={isPending}
				onClose={() => setDeleteOpen(false)}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
};
